/**
 * Styling constants for the Switchboard UI components.
 * Centralizes magic numbers for consistency and maintainability.
 */

// =============================================================================
// Edge Styling
// =============================================================================

/** Default stroke width for edges in the canvas */
export const EDGE_STROKE_WIDTH = 2

/** Stroke width when edge is highlighted (e.g., duplicate connection warning) */
export const EDGE_STROKE_WIDTH_HIGHLIGHTED = 4

/**
 * Stroke width on hover.
 * Note: This value is used in react-flow.css - keep in sync if changed.
 * @see {@link file://./styles/react-flow.css} .react-flow__edge:hover .react-flow__edge-path
 */
export const EDGE_STROKE_WIDTH_HOVER = 4

// =============================================================================
// Edge Colors (Chakra token names)
// =============================================================================

/** Default edge stroke color token (brand.focusRing) */
export const EDGE_STROKE_COLOR_TOKEN = 'brand.focusRing'

/** Edge stroke color token on hover (brand.fg) */
export const EDGE_STROKE_COLOR_HOVER_TOKEN = 'brand.fg'

/** Width of the invisible interaction area around edges for easier clicking */
export const EDGE_INTERACTION_WIDTH = 36

// =============================================================================
// Node Styling
// =============================================================================

/**
 * Node card opacity - slightly transparent to show edges behind when overlapping.
 * Value chosen to be nearly opaque while still allowing edge visibility.
 */
export const NODE_CARD_OPACITY = 0.9

/**
 * Node card shadow token name.
 * Uses the semantic 'emphasized' shadow token for depth and visual hierarchy.
 * @see defaultTheme.ts shadows.emphasized
 */
export const NODE_CARD_SHADOW =
  '0 8px 24px rgba(0, 0, 0, 0.25), 0 4px 8px rgba(0, 0, 0, 0.15)'

export const NODE_CARD_BORDER_RADIUS = 'lg'

// =============================================================================
// Z-Index Layers
// =============================================================================

/**
 * Z-index values for UI elements.
 * Uses distinct values to ensure proper stacking without conflicts.
 *
 * Stacking order (lowest to highest):
 * - Node elements (labels, buttons): 10-11
 * - Grab indicator: 50 (above nodes, below modals)
 * - Modal backdrop: 2000 (dialog sits one layer above)
 *
 * Switchboard dialogs can open inside web-app shells such as the Admin drawer
 * overlay, so modals must stay above those layers.
 */
export const Z_INDEX = {
  /** Handle labels within nodes */
  NODE_HANDLE_LABEL: 10,
  /** Delete button within nodes */
  NODE_DELETE_BUTTON: 11,
  /** Grab indicator during edge reconnection (above nodes, below modals) */
  GRAB_INDICATOR: 50,
  /** Modal backdrop; Modal places the dialog one layer above */
  MODAL: 2000,
} as const
